import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, Skeleton, Container, IconButton, Paper,
  useTheme, useMediaQuery
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import EditIcon from '@mui/icons-material/Edit';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { PersonalizedRecommendations, RecentlyViewed } from '../components/Recommendations';
import useBehaviorTracking from '../hooks/useBehaviorTracking';

// 修復圖片 URL（處理雙斜線問題）
const fixImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/300x300?text=商品圖片';
  // 修復雙斜線問題：https://www.ezp.com.tw//images -> https://www.ezp.com.tw/images
  return url.replace(/([^:]\/)\/+/g, '$1');
};

// Banner 輪播資料 - 全部使用 Gemini 生成的圖片
const getBanners = (freeShippingThreshold) => [
  {
    id: 1,
    image: '/images/banner-1.jpg',
    title: '優爾商城',
    subtitle: '讓家更整潔的整理魔法',
    description: '全館滿額享免運優惠'
  },
  {
    id: 2,
    image: '/images/banner-2.jpg',
    title: '品質嚴選',
    subtitle: '正品保證',
    description: '全站免運優惠中'
  },
  {
    id: 3,
    image: '/images/banner-3.jpg',
    title: '免運優惠',
    subtitle: `滿 NT$599 享超商免運`,
    description: '全館收納好物，提供超商取貨服務'
  }
];

// 預設分類區塊 (統一使用 MUI Icons)
const defaultCategoryBlocks = [
  { name: '熱銷特惠', icon: <WhatshotIcon />, color: '#e53935', path: '/products?sort=discount' },
  { name: '新品上架', icon: <NewReleasesIcon />, color: '#1976d2', path: '/products?sort=newest' },
];

// 分類 Icon 映射表
const categoryIconMap = {
  '廚房用品': <RestaurantIcon />,
  '收納整理': <Inventory2Icon />,
  '衛浴清潔': <HomeWorkIcon />,
  '文具辦公': <EditIcon />,
  '節慶活動': <LocalOfferIcon />,
  '居家生活': <HomeWorkIcon />,
  '旅行用品': <LocalShippingIcon />,
  '美妝美容': <FaceRetouchingNaturalIcon />,
  '休閒娛樂': <WhatshotIcon />,
  '學生用品': <EditIcon />,
  '季節商品': <NewReleasesIcon />,
  '其他': <LocalOfferIcon />,
  '汽車用品': <LocalShippingIcon />,
  '寵物用品': <LocalOfferIcon />,
  '五金工具': <HomeWorkIcon />,
};

// 分類顏色陣列
const categoryColors = ['#00796b', '#5d4037', '#388e3c', '#f57c00', '#c2185b', '#7b1fa2', '#0288d1', '#689f38'];

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [hotProducts, setHotProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBanner, setCurrentBanner] = useState(0);
  const { addToCart } = useCart();
  const { shippingConfig, categories: dbCategories } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const bannerRef = useRef(null);
  useBehaviorTracking();

  // 動態生成分類區塊 - 結合預設分類和資料庫分類
  const categoryBlocks = React.useMemo(() => {
    const dynamicCats = dbCategories.map((cat, idx) => ({
      name: cat.name,
      icon: categoryIconMap[cat.name] || <LocalOfferIcon />,
      color: categoryColors[idx % categoryColors.length],
      path: `/products?category=${encodeURIComponent(cat.name)}`
    }));
    return [...defaultCategoryBlocks, ...dynamicCats];
  }, [dbCategories]);

  // 使用動態免運門檻生成 banners
  const banners = getBanners(shippingConfig?.free_shipping_threshold || 799);

  // Banner 自動輪播
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, newRes, hotRes] = await Promise.all([
          api.get('/products/featured?limit=8'),
          api.get('/products?sort=newest&limit=8'),
          api.get('/products?sort=sales&limit=8')
        ]);
        setFeatured(featuredRes.data);
        setNewProducts(newRes.data.items || []);
        setHotProducts(hotRes.data.items || []);
      } catch (error) {
        console.error('取得商品失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const calculateDiscount = (original, sale) => {
    if (!original || original <= 0 || !sale) return 0;
    const discount = Math.round((1 - sale / original) * 100);
    return isNaN(discount) || !isFinite(discount) ? 0 : discount;
  };

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  // 商品卡片組件
  const ProductCard = ({ product }) => {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          bgcolor: 'white',
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          border: '1px solid #eee',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
          }
        }}
      >
        {/* 商品圖片 */}
        <Box
          sx={{
            position: 'relative',
            pt: '100%',
            bgcolor: '#f9f9f9',
            cursor: 'pointer'
          }}
          onClick={() => navigate(`/products/${product.seo_url || product.id}`)}
        >
          <CardMedia
            component="img"
            image={fixImageUrl(product.image_url)}
            alt={product.name}
            loading="lazy"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              p: 2
            }}
          />
        </Box>

        {/* 商品資訊 */}
        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              minHeight: 40,
              lineHeight: 1.4,
              color: '#333',
              cursor: 'pointer',
              '&:hover': { color: '#1769ff' }
            }}
            onClick={() => navigate(`/products/${product.seo_url || product.id}`)}
          >
            {product.name}
          </Typography>

          {/* 價格區 - 只顯示售價 */}
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#e53935',
                fontWeight: 'bold',
                fontSize: '1.25rem'
              }}
            >
              ${product.sale_price?.toLocaleString()}
            </Typography>
          </Box>

          {/* 配送標籤 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <Chip
              icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
              label="快速到貨"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: '#e3f2fd',
                color: '#1769ff',
                '& .MuiChip-icon': { color: '#1769ff' }
              }}
            />
            {product.quantity <= 10 && product.quantity > 0 && (
              <Chip
                label={`剩${product.quantity}件`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: '#fff3e0',
                  color: '#f57c00'
                }}
              />
            )}
          </Box>
        </CardContent>

        {/* 加入購物車按鈕 */}
        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<ShoppingCartIcon />}
            onClick={async () => {
              const success = await addToCart(product.id);
              if (!success || !window.dataLayer) return;
              window.dataLayer.push({ ecommerce: null });
              window.dataLayer.push({
                event: 'add_to_cart',
                ecommerce: {
                  currency: 'TWD',
                  value: product.sale_price,
                  items: [{
                    item_id: product.sku || product.id?.toString(),
                    item_name: product.name,
                    item_category: product.category_name,
                    price: product.sale_price,
                    quantity: 1
                  }]
                }
              });
            }}
            disabled={product.quantity <= 0}
            sx={{
              bgcolor: product.quantity > 0 ? '#1769ff' : '#ccc',
              '&:hover': { bgcolor: product.quantity > 0 ? '#1557cc' : '#ccc' },
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            {product.quantity > 0 ? '加入購物車' : '已售完'}
          </Button>
        </Box>
      </Card>
    );
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5' }}>
      {/* 快速分類區塊 - 水平滾動 */}
      <Box
        sx={{
          bgcolor: 'white',
          py: 1.5,
          borderBottom: '1px solid #eee',
          overflowX: 'auto',
          '&::-webkit-scrollbar': { height: 0 }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 2, minWidth: 'max-content' }}>
            {categoryBlocks.map((cat) => (
              <Box
                key={cat.name}
                onClick={() => navigate(cat.path)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  minWidth: 70,
                  '&:hover': { '& .cat-icon': { transform: 'scale(1.1)' } }
                }}
              >
                <Box
                  className="cat-icon"
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: `${cat.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: cat.color,
                    transition: 'transform 0.2s',
                    '& svg': { fontSize: 24 }
                  }}
                >
                  {cat.icon}
                </Box>
                <Typography variant="caption" sx={{ mt: 0.5, color: '#333', fontWeight: 500 }}>
                  {cat.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Hero Banner 輪播 */}
      <Container maxWidth="lg" sx={{ pt: 2 }}>
        <Box
          ref={bannerRef}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            bgcolor: '#000',
            borderRadius: 3
          }}
        >
        <Box
          sx={{
            display: 'flex',
            transition: 'transform 0.5s ease',
            transform: `translateX(-${currentBanner * 100}%)`
          }}
        >
          {banners.map((banner) => (
            <Box
              key={banner.id}
              sx={{
                minWidth: '100%',
                // 支援圖片或漸層背景
                background: banner.image
                  ? `url(${banner.image}) center center / cover no-repeat`
                  : banner.bgcolor,
                py: banner.image ? 0 : { xs: 4, md: 6 },
                px: banner.image ? 0 : 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: { xs: 180, md: 280 },
                cursor: 'pointer'
              }}
              onClick={() => navigate('/products')}
              role="button"
              aria-label={`${banner.title} - ${banner.subtitle}`}
            >
              {/* 圖片模式：不顯示文字覆蓋，圖片本身已包含設計 */}
              {!banner.image && (
                <>
                  <Chip
                    label={banner.tag}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      mb: 2,
                      fontWeight: 'bold'
                    }}
                  />
                  <Typography
                    variant={isMobile ? 'h4' : 'h2'}
                    sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}
                  >
                    {banner.title}
                  </Typography>
                  <Typography
                    variant={isMobile ? 'h6' : 'h4'}
                    sx={{ color: 'white', mt: 1, textAlign: 'center' }}
                  >
                    {banner.subtitle}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: 'rgba(255,255,255,0.8)', mt: 1, textAlign: 'center' }}
                  >
                    {banner.description}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={(e) => { e.stopPropagation(); navigate('/products'); }}
                    sx={{
                      mt: 3,
                      bgcolor: 'white',
                      color: '#333',
                      fontWeight: 'bold',
                      px: 4,
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                  >
                    立即選購
                  </Button>
                </>
              )}
            </Box>
          ))}
        </Box>

        {/* 輪播控制按鈕 */}
        <IconButton
          onClick={prevBanner}
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'white' }
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton
          onClick={nextBanner}
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'white' }
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>

        {/* 輪播指示點 */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1
          }}
        >
          {banners.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentBanner(index)}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: currentBanner === index ? 'white' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
            />
          ))}
        </Box>
      </Box>
      </Container>

      {/* 優惠資訊條 */}
      <Container maxWidth="lg">
        <Box
          sx={{
            bgcolor: 'white',
            py: 2,
            my: 2,
            borderRadius: 2
          }}
        >
          <Grid container spacing={2} justifyContent="center">
            {[
              { icon: <LocalShippingIcon />, text: `滿 $${shippingConfig?.free_shipping_threshold || 499} 免運`, color: '#1769ff' },
              { icon: <LocalOfferIcon />, text: '正品保證', color: '#4caf50' },
              { icon: <SavingsIcon />, text: '批發優惠', color: '#e53935' },
              { icon: <TrendingUpIcon />, text: '快速到貨', color: '#ff9800' }
            ].map((item, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Box sx={{ color: item.color }}>{item.icon}</Box>
                  <Typography variant="body2" fontWeight="medium">{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        {/* 今日發現 - 精選商品 */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferIcon sx={{ color: '#e53935' }} />
              <Typography variant="h6" fontWeight="bold">
                今日發現
              </Typography>
              <Typography variant="body2" color="text.secondary">
                精選最熱商品！
              </Typography>
            </Box>
            <Button
              onClick={() => navigate('/products')}
              sx={{ color: '#1769ff' }}
            >
              查看更多 &gt;
            </Button>
          </Box>

          <Grid container spacing={2}>
            {loading
              ? Array(8).fill(0).map((_, i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <Card sx={{ borderRadius: 2 }}>
                      <Skeleton variant="rectangular" sx={{ pt: '100%' }} />
                      <CardContent>
                        <Skeleton width="80%" />
                        <Skeleton width="60%" />
                        <Skeleton width="40%" />
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              : featured.map(product => (
                  <Grid item xs={6} sm={4} md={3} key={product.id}>
                    <ProductCard product={product} />
                  </Grid>
                ))
            }
          </Grid>
        </Box>

        {/* 熱銷排行 */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WhatshotIcon sx={{ color: '#ff5722' }} />
              <Typography variant="h6" fontWeight="bold">
                熱銷排行
              </Typography>
              <Typography variant="body2" color="text.secondary">
                大家都在買！
              </Typography>
            </Box>
            <Button
              onClick={() => navigate('/products?sort=sales')}
              sx={{ color: '#1769ff' }}
            >
              查看更多 &gt;
            </Button>
          </Box>

          <Grid container spacing={2}>
            {loading
              ? Array(4).fill(0).map((_, i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <Card sx={{ borderRadius: 2 }}>
                      <Skeleton variant="rectangular" sx={{ pt: '100%' }} />
                      <CardContent>
                        <Skeleton width="80%" />
                        <Skeleton width="60%" />
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              : hotProducts.slice(0, 4).map((product, index) => (
                  <Grid item xs={6} sm={4} md={3} key={product.id}>
                    <Box sx={{ position: 'relative' }}>
                      {/* 排名標籤 */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 28,
                          height: 28,
                          bgcolor: index < 3 ? '#e53935' : '#666',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '0.85rem',
                          zIndex: 2
                        }}
                      >
                        {index + 1}
                      </Box>
                      <ProductCard product={product} />
                    </Box>
                  </Grid>
                ))
            }
          </Grid>
        </Box>

        {/* 新品上架 */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NewReleasesIcon sx={{ color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="bold">
                新品上架
              </Typography>
              <Typography variant="body2" color="text.secondary">
                搶鮮看新貨！
              </Typography>
            </Box>
            <Button
              onClick={() => navigate('/products?sort=newest')}
              sx={{ color: '#1769ff' }}
            >
              查看更多 &gt;
            </Button>
          </Box>

          <Grid container spacing={2}>
            {!loading && newProducts.slice(0, 4).map(product => (
              <Grid item xs={6} sm={4} md={3} key={`new-${product.id}`}>
                <ProductCard product={product} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* 最近瀏覽 */}
        <RecentlyViewed limit={8} />

        {/* 個人化推薦 */}
        <PersonalizedRecommendations limit={8} title="猜你喜歡" />

        {/* 品牌保證區塊 (統一使用 MUI Icons) */}
        <Paper sx={{ p: 4, mt: 4, borderRadius: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={4} justifyContent="center">
            {[
              { icon: <VerifiedIcon />, title: '正品保證', desc: '嚴選品質 安心購', color: '#4caf50' },
              { icon: <LocalShippingIcon />, title: '快速配送', desc: '2-3天送達', color: '#1769ff' },
              { icon: <CreditCardIcon />, title: '安心付款', desc: '超商取貨付款', color: '#ff9800' },
              { icon: <SupportAgentIcon />, title: '專業客服', desc: '週一至週五 9-18', color: '#9c27b0' }
            ].map((item, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: item.color, mb: 1, '& svg': { fontSize: 40 } }}>{item.icon}</Box>
                  <Typography variant="subtitle1" fontWeight="bold">{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default Home;
